import React, { useState, useRef } from 'react';
import { Form, Input, Button, DatePicker, TimePicker, Upload, message, Radio, Space, Tag, Select } from 'antd';
import { UploadOutlined, FileImageOutlined, DeleteOutlined, PlusOutlined, CloseOutlined, DownOutlined } from '@ant-design/icons';
import moment from 'moment-timezone';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { checkEdrpouCode, submitPublicRequest } from '../api/publicRequests';
import ContactsSection from './ContactsSection';
import LoadingZoneSelect from './LoadingZoneSelect';

moment.tz.setDefault('Europe/Kiev');

const startRequestTime = parseInt(process.env.REACT_APP_START_REQUEST_TIME || '9');
const endRequestTime = parseInt(process.env.REACT_APP_END_REQUEST_TIME || '16');

const CAR_PLATE_PATTERN = /^[А-ЯҐЄІЇа-яґєії'A-Z]{2}\s?\d{4}\s?[А-ЯҐЄІЇа-яґєії'A-Z]{2}$/i;

const disabledDate = (current) => {
  return current && current < moment().endOf('day');
};

/** Валідатор для RangePicker */
const makeRangeValidator = (fieldName) => ({
  validator(_, value) {
    if (!value || !value[0] || !value[1]) return Promise.resolve();
    const [start, end] = value;
    if (end.isSameOrBefore(start)) {
      return Promise.reject(new Error('Час завершення має бути пізніше початку'));
    }
    const maxHours = fieldName === 'removalRange' || fieldName === 'deliveryRange' ? 7 * 24 : 24;
    const diffHours = end.diff(start, 'hours');
    if (diffHours > maxHours) {
      if (fieldName === 'removalRange' || fieldName === 'deliveryRange') {
        return Promise.reject(new Error(`Різниця між початком і кінцем ${fieldName === 'removalRange' ? 'демонтажу' : 'монтажу'} не може перевищувати 7 днів`));
      }
      return Promise.reject(new Error('Різниця між початком і кінцем не може перевищувати 24 години'));
    }
    return Promise.resolve();
  },
});

/** Для RangePicker — блокуємо години «end», щоб вони не були раніше «start» на тому ж дні */
const makeRangeDisabledTime = (dates, type) => {
  if (!dates || !dates[0] || !dates[1]) return {};
  const start = dates[0];
  const end   = dates[1];

  if (type === 'end') {
    // Якщо вибрана та сама дата — блокуємо години <= start
    const sameDay = start.isSame(end, 'day');
    if (sameDay) {
      const startHour = start.hour();
      return {
        disabledHours: () => Array.from({ length: startHour + 1 }, (_, i) => i),
      };
    }
  }
  return {};
};

const EventForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [hasAdditionalInfo, setHasAdditionalInfo] = useState(false);
  const [isWorkTime, setIsWorkTime] = useState(true);
  const [timeLeft, setTimeLeft] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Список номерів авто
  const [carPlates, setCarPlates] = useState(['']);

  // useRef щоб синхронно читати вибрані дати в disabledTime (устава — асинхронна)
  const deliveryDatesRef = useRef(null);
  const removalDatesRef  = useRef(null);
  // Чи торкнувся користувач поля демонтажу (щоб не показувати помилку завчасно)
  const [removalTouched, setRemovalTouched] = useState(false);

  // Реактивне відстеження для disabled-стану пікерів
  const watchedEventDate    = Form.useWatch('eventDate', form);
  const watchedEventTime    = Form.useWatch('eventTime', form);
  const watchedDeliveryRange = Form.useWatch('deliveryRange', form);

  // Пікер завезення активний онли після вибору дати та часу проведення
  const isDeliveryEnabled = !!watchedEventDate && !!(watchedEventTime && watchedEventTime[0] && watchedEventTime[1]);
  // Пікер демонтажу активний онли після заповнення завезення
  const isRemovalEnabled  = !!(watchedDeliveryRange && watchedDeliveryRange[0] && watchedDeliveryRange[1]);

  const disabledDeliveryDate = (current) => {
    if (!current) return false;
    // Блокуємо минулі дати (до вчора включно)
    if (current.clone().startOf('day').isBefore(moment().startOf('day'))) return true;

    // Блокуємо дати ПІСЛЯ дня проведення (сам день дозволений — але тільки до початку заходу)
    // Порівнюємо лише дату, без часу (current може мати ненульовий час)
    const eventDate = form.getFieldValue('eventDate');
    if (eventDate && current.clone().startOf('day').isAfter(eventDate.clone().startOf('day'))) return true;

    // Обмеження: не більше 7 днів від початку монтажу
    if (deliveryDatesRef.current && deliveryDatesRef.current[0] && !deliveryDatesRef.current[1]) {
      const maxEnd = deliveryDatesRef.current[0].clone().add(7, 'days').endOf('day');
      if (current.isAfter(maxEnd)) return true;
    }
    return false;
  };


  const disabledDeliveryTime = (date, type) => {
    const eventDate      = form.getFieldValue('eventDate');
    const eventTime      = form.getFieldValue('eventTime');
    const eventStartHour = eventDate && eventTime && eventTime[0] ? eventTime[0].hour() : null;

    // antd передає moment як перший аргумент, якщо ні — беремо з ref
    const getDate = (idx) => {
      if (date && typeof date.isSame === 'function') return date;
      return deliveryDatesRef.current && deliveryDatesRef.current[idx];
    };

    if (type === 'start') {
      const startDate  = getDate(0);
      const isEventDay = eventDate && startDate &&
        startDate.clone().startOf('day').isSame(eventDate.clone().startOf('day'));

      if (isEventDay && eventStartHour !== null) {
        // Початок монтажу: блокуємо >= початку заходу
        return { disabledHours: () => Array.from({ length: 24 - eventStartHour }, (_, i) => eventStartHour + i) };
      }
      return {};
    }

    if (type === 'end') {
      const startDate = deliveryDatesRef.current && deliveryDatesRef.current[0];
      // date від antd — це поточна дата панелі (може бути null якщо ще не вибрано)
      const endDate = (date && typeof date.isSame === 'function')
        ? date
        : (deliveryDatesRef.current && deliveryDatesRef.current[1]);

      let blocked = [];

      // Якщо початок і кінець на одному дні — блокуємо <= startHour
      if (startDate && endDate &&
          startDate.clone().startOf('day').isSame(endDate.clone().startOf('day'))) {
        blocked = Array.from({ length: startDate.hour() + 1 }, (_, i) => i);
      }

      // Перевіряємо чи кінець — день заходу
      const isEndEventDay = eventDate && endDate &&
        endDate.clone().startOf('day').isSame(eventDate.clone().startOf('day'));

      // Якщо кінець ще не вибрано — блокуємо >= eventStartHour як safe default
      // (disabledDate вже не дає вибрати дату після заходу)
      if ((isEndEventDay || !endDate) && eventStartHour !== null) {
        const eventBlocked = Array.from({ length: 24 - eventStartHour }, (_, i) => eventStartHour + i);
        blocked = [...new Set([...blocked, ...eventBlocked])];
      }

      return { disabledHours: () => blocked };
    }

    return {};
  };


  const disabledEventDate = (current) => {
    if (!current) return false;
    let baseDisabled = current.isBefore(moment().endOf('day'));
    const deliveryRange = form.getFieldValue('deliveryRange');
    
    if (deliveryRange && deliveryRange[1]) {
      if (current.isBefore(deliveryRange[1].clone().startOf('day'))) return true;
    }
    return baseDisabled;
  };

  const disabledEventTime = (_, type) => {
    const eventDate = form.getFieldValue('eventDate');
    const eventTime = form.getFieldValue('eventTime');
    const deliveryRange = form.getFieldValue('deliveryRange');

    let disabledStartHours = [];
    if (eventDate && deliveryRange && deliveryRange[1] && eventDate.isSame(deliveryRange[1], 'day')) {
      disabledStartHours = Array.from({ length: deliveryRange[1].hour() + 1 }, (_, i) => i);
    }

    if (type === 'start') {
      return { disabledHours: () => disabledStartHours };
    }

    if (type === 'end') {
      const startHour = eventTime && eventTime[0] ? eventTime[0].hour() : -1;
      let disabledEndHours = [];
      if (startHour >= 0) {
        disabledEndHours = Array.from({ length: startHour + 1 }, (_, i) => i);
      }
      const allBlocked = Array.from(new Set([...disabledStartHours, ...disabledEndHours]));
      return { disabledHours: () => allBlocked };
    }
    return {};
  };

  const disabledRemovalDate = (current) => {
    if (!current) return false;
    if (current.clone().startOf('day').isBefore(moment().startOf('day'))) return true;

    // Блокуємо дні ДО дня завершення заходу (сам день завершення дозволений — до часу кінця)
    const eventDate = form.getFieldValue('eventDate');
    if (eventDate && current.clone().startOf('day').isBefore(eventDate.clone().startOf('day'))) return true;

    // Обмеження: не більше 7 днів від початку демонтажу
    if (removalDatesRef.current && removalDatesRef.current[0] && !removalDatesRef.current[1]) {
      const maxEnd = removalDatesRef.current[0].clone().add(7, 'days').endOf('day');
      if (current.isAfter(maxEnd)) return true;
    }
    return false;
  };

  const disabledRemovalTime = (date, type) => {
    const eventDate    = form.getFieldValue('eventDate');
    const eventTime    = form.getFieldValue('eventTime');
    const eventEndHour = eventDate && eventTime && eventTime[1] ? eventTime[1].hour() : null;

    // Визначаємо дату панелі (антд передає moment, якщо ні — беремо з ref)
    const getDate = (idx) => {
      if (date && typeof date.isSame === 'function') return date;
      return removalDatesRef.current && removalDatesRef.current[idx];
    };

    if (type === 'start') {
      const startDate  = getDate(0);
      const isEventDay = eventDate && startDate &&
        startDate.clone().startOf('day').isSame(eventDate.clone().startOf('day'));

      // Якщо дату ще не вибрано — блокуємо < eventEndHour як safe default
      if ((isEventDay || !startDate) && eventEndHour !== null) {
        // Початок демонтажу: блокуємо < eventEndHour (до завершення заходу)
        return { disabledHours: () => Array.from({ length: eventEndHour }, (_, i) => i) };
      }
      return {};
    }

    if (type === 'end') {
      const startDate = removalDatesRef.current && removalDatesRef.current[0];
      const endDate = (date && typeof date.isSame === 'function')
        ? date
        : (removalDatesRef.current && removalDatesRef.current[1]);

      let blocked = [];

      // Якщо початок і кінець на одному дні — блокуємо <= startHour
      if (startDate && endDate &&
          startDate.clone().startOf('day').isSame(endDate.clone().startOf('day'))) {
        blocked = Array.from({ length: startDate.hour() + 1 }, (_, i) => i);
      }

      // Якщо кінець не вибрано або кінець = день заходу: блокуємо < eventEndHour як safe default
      const isEndEventDay = eventDate && endDate &&
        endDate.clone().startOf('day').isSame(eventDate.clone().startOf('day'));

      if ((isEndEventDay || !endDate) && eventEndHour !== null) {
        // Завершення демонтажу: не раніше завершення заходу
        const eventBlocked = Array.from({ length: eventEndHour }, (_, i) => i);
        blocked = [...new Set([...blocked, ...eventBlocked])];
      }

      return { disabledHours: () => blocked };
    }

    return {};
  };

  React.useEffect(() => {
    const checkWorkTime = () => {
      const now = moment();
      const day = now.day();
      const hour = now.hour();
      const isWorkDay = day >= 1 && day <= 5;
      const isWorkHour = hour >= startRequestTime && hour < endRequestTime;

      if (isWorkDay && isWorkHour) {
        setIsWorkTime(true);
      } else {
        setIsWorkTime(false);
        calculateTimeLeft();
      }
    };

    const calculateTimeLeft = () => {
      const now = moment();
      let nextWorkTime;
      if (now.day() === 6) nextWorkTime = moment().day(8).startOf('day').hour(startRequestTime);
      else if (now.day() === 0) nextWorkTime = moment().day(1).startOf('day').hour(startRequestTime);
      else if (now.hour() >= endRequestTime) nextWorkTime = moment().add(1, 'days').startOf('day').hour(startRequestTime);
      else nextWorkTime = moment().startOf('day').hour(startRequestTime);

      const duration = moment.duration(nextWorkTime.diff(now));
      const hours = Math.floor(duration.asHours());
      const minutes = Math.floor(duration.asMinutes()) % 60;
      setTimeLeft(`${hours} год ${minutes} хв`);
    };

    checkWorkTime();
    const interval = setInterval(checkWorkTime, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleBeforeUpload = (file) => {
    setFileList((prev) => [...prev, file]);
    return false;
  };

  const handleRemove = (uid) => {
    setFileList((prev) => prev.filter((f) => f.uid !== uid));
  };

  const handleEdrpouBlur = async (e) => {
    const edrpou = e.target.value;
    if (edrpou && edrpou.length >= 8) {
      try {
        const res = await checkEdrpouCode(edrpou);
        if (res && res.success === false) {
          message.warning(res.message || 'Договір не знайдено');
          form.setFields([{ name: 'Account', errors: [res.message || 'Договір не знайдено'] }]);
        } else {
          message.success(res.message || 'Договір знайдено ✓');
          form.setFields([{ name: 'Account', errors: [] }]);
        }
      } catch {
        message.warning('Договір не знайдено');
        form.setFields([{ name: 'Account', errors: ['Договір не знайдено'] }]);
      }
    }
  };

  // ─── Авто: управление списком ─────────────────────────────
  const addCarPlate = () => setCarPlates((prev) => [...prev, '']);
  const removeCarPlate = (idx) => setCarPlates((prev) => prev.filter((_, i) => i !== idx));
  const updateCarPlate = (idx, val) =>
    setCarPlates((prev) => prev.map((v, i) => (i === idx ? val : v)));

  const onFinish = async (values) => {
    if (form.getFieldError('Account').length > 0) {
      message.error('Відправка заблокована: договір не знайдено.');
      return;
    }

    if (!executeRecaptcha) {
      message.error('reCAPTCHA ще не готова. Спробуйте пізніше.');
      return;
    }

    // Валідація авто
    const validPlates = carPlates.map((p) => p.trim()).filter(Boolean);
    for (const plate of validPlates) {
      if (!CAR_PLATE_PATTERN.test(plate)) {
        message.error(`Невірний формат номеру: ${plate}. Очікується формат АА 0000 АА`);
        return;
      }
    }

    setLoading(true);
    try {
      const gToken = await executeRecaptcha('event_submit');
      
      const payload = {
        ...values,
        type: 'event',
        SuServiceSubCategory: 'b3a00521-5ce9-4d61-9f6a-165dd5d4c40f',
        'g-recaptcha-response': gToken,
      };

      // Запобігаємо дублюванню відповідального в Creatio
      if (payload.isApplicantResponsible) {
         payload.isApplicantResponsible = false;
      }

      if (values.eventDate && values.eventTime) {
        payload.SuStartWork = values.eventDate.clone().hour(values.eventTime[0].hour()).minute(0).second(0).toISOString();
        payload.SuEndWork   = values.eventDate.clone().hour(values.eventTime[1].hour()).minute(0).second(0).toISOString();
      }

      // Монтаж (RangePicker)
      if (values.deliveryRange && values.deliveryRange[0] && values.deliveryRange[1]) {
        payload.RespEquipmentDeliveryDate = values.deliveryRange[0].clone().minute(0).second(0).toISOString();
        payload.RespEquipmentDeliveryEnd  = values.deliveryRange[1].clone().minute(0).second(0).toISOString();
      }

      // Демонтаж (RangePicker)
      if (values.removalRange && values.removalRange[0] && values.removalRange[1]) {
        payload.RespEquipmentRemovalDate = values.removalRange[0].clone().minute(0).second(0).toISOString();
        payload.RespEquipmentRemovalEnd  = values.removalRange[1].clone().minute(0).second(0).toISOString();
      }

      // Номери авто — через кому
      if (validPlates.length > 0) {
        payload.SuCarNumber = validPlates.join(', ');
      }
      delete payload.deliveryRange;
      delete payload.removalRange;
      // hasAdditionalInfoChoice потрапляє в payload через ...values — видаляємо, виставляємо явно
      delete payload.hasAdditionalInfoChoice;
      payload.RespHasNotes = hasAdditionalInfo === true || fileList.length > 0;

      if (fileList.length > 0) {
        const convertBase64 = (file) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
          });

        payload.files = [];
        for (const f of fileList) {
          const rawFile = f.originFileObj || f;
          const base64Str = await convertBase64(rawFile);
          payload.files.push({ file: base64Str, fileName: f.name });
        }
      }

      await submitPublicRequest(JSON.stringify(payload));
      message.success('Бланк успішно надіслано!');
      form.resetFields();
      setFileList([]);
      setCarPlates(['']);
      setHasAdditionalInfo(false);
    } catch (error) {
      console.error('Submit Error:', error);
      message.error('Сталася помилка при надсиланні.');
    } finally {
      setLoading(false);
    }
  };

  if (!isWorkTime) {
    return (
      <div className="non-work-time-message" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 500, marginBottom: '12px' }}>Створення звернень заблоковано</h2>
        <p style={{ color: '#595959', fontSize: '16px', lineHeight: '28px' }}>
          Подання звернень наразі недоступне.
          <br /> Ми приймаємо їх у <span style={{ fontWeight: 600, color: '#000' }}>будні з {startRequestTime}:00 до {endRequestTime}:00</span>.
        </p>
        <p style={{ color: '#595959', fontSize: '16px', lineHeight: '28px' }}>
          Спробуйте, будь ласка, через <span style={{ fontWeight: 600, color: '#000' }}>{timeLeft}</span>.
        </p>
      </div>
    );
  }

  return (
    <Form form={form} layout="vertical" onFinish={onFinish} className="public-form">

      {/* 1 ─── Контактна інформація ───────────────── */}
      <div className="form-section-title">Контактна інформація</div>
      <ContactsSection />

      {/* 2 ─── Підрядники та контрагенти ─────────── */}
      <div className="form-section-title">Підрядники та контрагенти</div>
      <div className="form-grid">
        <Form.Item
          name="Account"
          label="ТОВ/ФОП з договором з ТРЦ (ЄДРПОУ)"
          rules={[
            { required: true, message: 'Введіть ЄДРПОУ' },
            { pattern: /^\d{8}$|^\d{10}$/, message: 'ЄДРПОУ має містити 8 або 10 цифр' },
          ]}
        >
          <Input placeholder="00000000" onBlur={handleEdrpouBlur} maxLength={10} />
        </Form.Item>
        <Form.Item name="RespMountingContractor" label="ТОВ / ФОП – підрядна організація">
          <Input placeholder="Назва підрядника" />
        </Form.Item>
      </div>

      {/* 3 ─── Інформація про захід ───────────────── */}
      <div className="form-section-title">Інформація про захід</div>
      <div className="form-grid">
        <Form.Item name="Subject" label="Назва заходу" rules={[{ required: true, message: 'Заповніть поле' }]}>
          <Input placeholder="Назва" />
        </Form.Item>
        <Form.Item name="SuWorkDescription" label="Локація та формат проведення" rules={[{ required: true, message: 'Заповніть поле' }]}>
          <Input placeholder="Наприклад: атріум 1-го поверху, ярмарок" />
        </Form.Item>
        <Form.Item name="eventDate" label="Дата проведення" dependencies={['deliveryRange']} rules={[{ required: true, message: 'Оберіть дату' }]}>
          <DatePicker
            style={{ width: '100%' }}
            format="DD.MM.YYYY"
            placeholder="Оберіть дату"
            disabledDate={disabledEventDate}
            showToday={false}
            onChange={() => {
              // Перевалідуємо eventTime (залежність від дати проведення)
              // removalRange не валідуємо автоматично, щоб не показувати помилку завчасно
              form.validateFields(['eventTime']).catch(() => {});
            }}
            suffixIcon={<DownOutlined />}
          />
        </Form.Item>
        <Form.Item
          name="eventTime"
          label="Час проведення"
          dependencies={['eventDate', 'deliveryRange']}
          rules={[{ required: true, message: '' }]}
          normalize={(values) => {
            if (!values) return values;
            return [
              values[0] ? values[0].clone().minute(0).second(0).millisecond(0) : values[0],
              values[1] ? values[1].clone().minute(0).second(0).millisecond(0) : values[1],
            ];
          }}
        >
          <TimePicker.RangePicker
            style={{ width: '100%' }}
            format="HH:00"
            showNow={false}
            placeholder={['Час початку', 'Час кінця']}
            hideDisabledOptions
            disabledTime={disabledEventTime}
            onChange={() => {
              form.validateFields(['removalRange']).catch(() => {});
            }}
          />
        </Form.Item>
        <div className="full-width">
          <Form.Item
            name="Symptoms"
            label="Що планується завезти / вивезти (кількість, призначення, характер робіт)"
            rules={[{ required: true, message: 'Заповніть поле' }]}
          >
            <Input.TextArea rows={2} placeholder="Опис обладнання, кількість, призначення або характер робіт..." />
          </Form.Item>
        </div>
      </div>

      {/* ─── Блок завантаження фото локації ──────── */}
      <div className="upload-section">
        <div className="upload-section-label">Фото локації</div>
        <Upload beforeUpload={handleBeforeUpload} showUploadList={false} multiple accept="image/*,.pdf">
          <Button icon={<UploadOutlined />}>Додати файли</Button>
        </Upload>
        {fileList.length > 0 && (
          <div className="upload-preview-list">
            {fileList.map((file) => (
              <div key={file.uid} className="upload-preview-item">
                <FileImageOutlined className="upload-preview-icon" />
                <span className="upload-preview-name">{file.name}</span>
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemove(file.uid)}
                  className="upload-preview-remove"
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 4 ─── Монтаж та демонтаж ────────────────── */}
      <div className="form-section-title">Монтаж та демонтаж</div>

      {/* Монтаж — RangePicker */}
      <Form.Item
        name="deliveryRange"
        label="Завезення / Монтаж (початок — завершення)"
        dependencies={['eventDate', 'eventTime']}
        rules={[
          { required: true, message: 'Оберіть період монтажу' },
          makeRangeValidator('deliveryRange'),
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || !value[1]) return Promise.resolve();
              const eventDate = getFieldValue('eventDate');
              const eventTime = getFieldValue('eventTime');
              if (eventDate && eventTime && eventTime[0]) {
                const eventStart = eventDate.clone().set({
                  hour: eventTime[0].hour(),
                  minute: 0,
                  second: 0,
                  millisecond: 0
                });
                if (value[1].isAfter(eventStart)) {
                  return Promise.reject(new Error('Монтаж має завершитися до початку заходу'));
                }
              }
              return Promise.resolve();
            },
          }),
        ]}
      >
        <DatePicker.RangePicker
          showTime={{
            hideDisabledOptions: true,
            format: 'HH:00',
            showNow: false,
            minuteStep: 60,
          }}
          style={{ width: '100%' }}
          format="DD.MM.YYYY HH:00"
          placeholder={['Початок монтажу', 'Завершення монтажу']}
          disabledDate={disabledDeliveryDate}
          disabledTime={disabledDeliveryTime}
          onCalendarChange={(dates) => { deliveryDatesRef.current = dates; }}
          disabled={!isDeliveryEnabled}
          inputReadOnly
          allowClear
          showToday={false}
          showNow={false}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              const eventDate = form.getFieldValue('eventDate');
              const eventTime = form.getFieldValue('eventTime');
              if (eventDate && eventTime && eventTime[0]) {
                const eventStartHour = eventTime[0].hour();
                const isStartEventDay = dates[0].clone().startOf('day').isSame(eventDate.clone().startOf('day'));
                const isEndEventDay   = dates[1].clone().startOf('day').isSame(eventDate.clone().startOf('day'));
                if ((isStartEventDay && dates[0].hour() >= eventStartHour) ||
                    (isEndEventDay   && dates[1].hour() >= eventStartHour)) {
                  form.setFieldValue('deliveryRange', null);
                  message.warning(`Монтаж має завершитись до ${eventStartHour}:00`);
                  return;
                }
              }
            }
            form.validateFields(['eventDate', 'eventTime']).catch(() => {});
          }}
          suffixIcon={<DownOutlined />}
        />
      </Form.Item>

      {/* Демонтаж — RangePicker */}
      <Form.Item
        name="removalRange"
        label="Вивезення / Демонтаж (початок — завершення)"
        dependencies={['deliveryRange', 'eventDate', 'eventTime']}
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              // Якщо поле ще не чіпали — не показуємо жодної помилки
              // (dependencies викликає перевалідацію, але ми ігноруємо її поки не touched)
              if (!removalTouched) {
                if (!value || !value[0]) return Promise.resolve();
              }

              // Перевірка обов'язковості (тільки якщо touched або submit)
              if (!value || !value[0] || !value[1]) {
                return removalTouched
                  ? Promise.reject(new Error('Оберіть період демонтажу'))
                  : Promise.resolve();
              }

              // Перевірка: кінець після початку та макс. 7 днів
              const [start, end] = value;
              if (end.isSameOrBefore(start)) {
                return Promise.reject(new Error('Час завершення має бути пізніше початку'));
              }
              const diffHours = end.diff(start, 'hours');
              if (diffHours > 7 * 24) {
                return Promise.reject(new Error('Різниця між початком і кінцем демонтажу не може перевищувати 7 днів'));
              }

              // Перевірка: після завершення заходу
              const eventDate = getFieldValue('eventDate');
              const eventTime = getFieldValue('eventTime');
              if (eventDate && eventTime && eventTime[1]) {
                const eventEnd = eventDate.clone().hour(eventTime[1].hour()).minute(0).second(0);
                if (value[0].isBefore(eventEnd)) {
                  return Promise.reject(new Error('Початок демонтажу має бути після завершення заходу'));
                }
              }

              // Перевірка: після завершення монтажу
              const deliveryRange = getFieldValue('deliveryRange');
              if (deliveryRange && deliveryRange[1]) {
                if (value[0].isSameOrBefore(deliveryRange[1])) {
                  return Promise.reject(new Error('Початок демонтажу має бути після завершення монтажу'));
                }
              }
              return Promise.resolve();
            },
          }),
        ]}
      >
        <DatePicker.RangePicker
          showTime={{
            hideDisabledOptions: true,
            format: 'HH:00',
            showNow: false,
            minuteStep: 60,
          }}
          style={{ width: '100%' }}
          format="DD.MM.YYYY HH:00"
          placeholder={['Початок демонтажу', 'Завершення демонтажу']}
          disabledDate={disabledRemovalDate}
          disabledTime={disabledRemovalTime}
          onCalendarChange={(dates) => { removalDatesRef.current = dates; }}
          onChange={(dates) => {
            setRemovalTouched(true);
            if (dates && dates[0] && dates[1]) {
              const eventDate = form.getFieldValue('eventDate');
              const eventTime = form.getFieldValue('eventTime');
              if (eventDate && eventTime && eventTime[1]) {
                const eventEndHour = eventTime[1].hour();
                const isStartEventDay = dates[0].clone().startOf('day').isSame(eventDate.clone().startOf('day'));
                // Початок демонтажу в день заходу має бути >= кінця заходу
                if (isStartEventDay && dates[0].hour() < eventEndHour) {
                  form.setFieldValue('removalRange', null);
                  message.warning(`Демонтаж може починатись не раніше ${eventEndHour}:00`);
                  return;
                }
              }
            }
            form.validateFields(['removalRange']).catch(() => {});
          }}
          disabled={!isRemovalEnabled}
          inputReadOnly
          allowClear
          showToday={false}
          showNow={false}
          suffixIcon={<DownOutlined />}
        />
      </Form.Item>

      {/* ─── Зона завантаження + Авто ─────────────── */}
      <div className="form-grid">
        <LoadingZoneSelect />

        {/* Мультиавто */}
        <div>
          <div className="ant-form-item-label" style={{ paddingBottom: 4 }}>
            <label>Номер авто (паркінг)</label>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {carPlates.map((plate, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Input
                  value={plate}
                  onChange={(e) => updateCarPlate(idx, e.target.value.toUpperCase())}
                  placeholder="АА 0000 АА"
                  maxLength={10}
                  style={{ flex: 1 }}
                  status={plate && !CAR_PLATE_PATTERN.test(plate) ? 'error' : ''}
                />
                {carPlates.length > 1 && (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => removeCarPlate(idx)}
                  />
                )}
              </div>
            ))}
            <Button
              type="dashed"
              size="small"
              icon={<PlusOutlined />}
              onClick={addCarPlate}
              style={{ alignSelf: 'flex-start' }}
            >
              Додати авто
            </Button>
          </div>
        </div>
      </div>

      {/* 5 ─── Додатково ─────────────────────────── */}
      <div className="form-section-title">Додатково</div>
      <div className="radio-group-section">
        <Form.Item
          name="RespAltitudeWorks"
          label="Висотні роботи"
          rules={[{ required: true, message: 'Оберіть варіант' }]}
        >
          <Radio.Group>
            <Radio value={true}>Так</Radio>
            <Radio value={false}>Ні</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="RespHasLadder"
          label="Наявність драбини"
          rules={[{ required: true, message: 'Оберіть варіант' }]}
        >
          <Radio.Group>
            <Radio value={true}>Так</Radio>
            <Radio value={false}>Ні</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="RespPowerGrid"
          label="Підключення до електромережі"
          rules={[{ required: true, message: 'Оберіть варіант' }]}
        >
          <Radio.Group>
            <Radio value={true}>Так</Radio>
            <Radio value={false}>Ні</Radio>
          </Radio.Group>
        </Form.Item>
      </div>

      <div className="radio-group-section" style={{ marginTop: 16 }}>
        <Form.Item
          name="hasAdditionalInfoChoice"
          label="Чи є додаткова інформація?"
          rules={[{ required: true, message: "Це поле є обов'язковим" }]}
        >
          <Radio.Group onChange={(e) => setHasAdditionalInfo(e.target.value)} value={hasAdditionalInfo}>
            <Radio value={true}>Так</Radio>
            <Radio value={false}>Ні</Radio>
          </Radio.Group>
        </Form.Item>
      </div>

      <div style={{ marginTop: 0 }}>
        {hasAdditionalInfo === true && (
          <Form.Item name="Notes" rules={[{ required: true, message: 'Заповніть додаткову інформацію' }]}>
            <Input.TextArea rows={3} placeholder="Вкажіть додаткову інформацію..." />
          </Form.Item>
        )}
      </div>

      <div className="form-submit-row">
        <Button type="primary" htmlType="submit" loading={loading} className="form-submit-btn">
          Надіслати бланк
        </Button>
        <div className="recaptcha-disclosure">
          This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy">Privacy Policy</a> and <a href="https://policies.google.com/terms">Terms of Service</a> apply.
        </div>
      </div>
    </Form>
  );
};

export default EventForm;
